import React from 'react';
import { Card, CardContent, CardHeader, Divider, Button, Box, Typography, Chip, Stack } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import HotelIcon from '@mui/icons-material/Hotel';
import HomeIcon from '@mui/icons-material/Home';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TripMap from './TripMap';
import { Trip } from '../types/Trip';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

interface TripMapViewProps {
   trip: Trip;
   height?: string | number;
}

const TripMapView: React.FC<TripMapViewProps> = ({ trip, height = 600 }) => {
   // Count how many accommodations we have to show in the title
   const accommodationsCount = trip.accommodations?.length || 0;

   // Count unique accommodation types
   const accommodationTypes =
      trip.accommodations?.reduce((acc: Record<string, number>, accommodation) => {
         const type = accommodation.type || 'other';
         acc[type] = (acc[type] || 0) + 1;
         return acc;
      }, {}) || {};

   // Helper to get appropriate icon for accommodation type
   const getAccommodationIcon = (type: string) => {
      switch (type) {
         case 'hotel':
            return <HotelIcon fontSize='small' sx={{ color: '#4caf50' }} />;
         case 'airbnb':
            return <HomeIcon fontSize='small' sx={{ color: '#ff5a5f' }} />;
         case 'apartment':
            return <ApartmentIcon fontSize='small' sx={{ color: '#9c27b0' }} />;
         default:
            return <LocationOnIcon fontSize='small' sx={{ color: '#2196f3' }} />;
      }
   };

   return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
         <CardHeader
            title='Trip Map'
            subheader={`${trip.title} - ${accommodationsCount} accommodation${accommodationsCount !== 1 ? 's' : ''}`}
            action={
               <Button
                  variant='outlined'
                  size='small'
                  startIcon={<MapIcon />}
                  onClick={() =>
                     window.open(
                        `https://www.google.com/maps/search/?api=1&query=${
                           trip.locations && trip.locations[0]?.name
                              ? encodeURIComponent(trip.locations[0].name)
                              : encodeURIComponent(trip.title)
                        }`,
                        '_blank'
                     )
                  }
               >
                  View in Google Maps
               </Button>
            }
            sx={{ pb: 1 }}
         />
         <Divider />

         {/* Map Legend */}
         {Object.keys(accommodationTypes).length > 0 && (
            <Box
               sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  height: 'max-content',
               }}
            >
               <Typography variant='body2' color='text.secondary' sx={{ mr: 1 }}>
                  Legend:
               </Typography>
               <Stack direction='row' spacing={1} flexWrap='wrap'>
                  <Chip
                     icon={<LocationOnIcon sx={{ color: '#1976D2 !important' }} />}
                     label='Trips'
                     size='small'
                     variant='outlined'
                     sx={{ borderColor: '#1976D2', color: '#1976D2' }}
                  />
                  <Chip
                     icon={<CameraAltIcon sx={{ color: '#1976D2 !important' }} />}
                     label='Trips Photos'
                     size='small'
                     variant='outlined'
                     sx={{ borderColor: '#1976D2', color: '#1976D2' }}
                  />

                  {Object.keys(accommodationTypes).map((type) => (
                     <Chip
                        key={type}
                        icon={<ApartmentIcon fontSize='small' sx={{ color: '#9c27b0' }} />}
                        label={'Accommodations'}
                        size='small'
                        variant='outlined'
                        sx={{
                           textTransform: 'capitalize',
                           borderColor:
                              type === 'hotel'
                                 ? '#4caf50'
                                 : type === 'airbnb'
                                   ? '#ff5a5f'
                                   : type === 'apartment'
                                     ? '#9c27b0'
                                     : '#2196f3',
                           color:
                              type === 'hotel'
                                 ? '#4caf50'
                                 : type === 'airbnb'
                                   ? '#ff5a5f'
                                   : type === 'apartment'
                                     ? '#9c27b0'
                                     : '#2196f3',
                        }}
                     />
                  ))}
               </Stack>
            </Box>
         )}

         <Divider />

         {/* Map Container - Takes up all the remaining space */}
         <Box
            sx={{
               flexGrow: 1,
               height: typeof height === 'number' ? `${height}px` : height,
               minHeight: '500px',
               position: 'relative',
            }}
         >
            <TripMap trip={trip} height='100%' />
         </Box>
      </Card>
   );
};

export default TripMapView; 